// Connection overlay (§14.2): "Connecting…", "Joining…", "Reconnecting… (3)" with a hopping bean,
// and "Disconnected — Back to menu". Sits above the screen stack.
import type { UiDeps } from '../deps';
import { beanIcon } from '../icons';
import { el, setHidden, setText, toggleClass } from '../ui';
import { button } from '../widgets';

export type ConnectionState = 'hidden' | 'connecting' | 'joining' | 'reconnecting' | 'disconnected';

const LABELS: Record<Exclude<ConnectionState, 'hidden'>, (attempt: number) => string> = {
  connecting: () => 'Connecting…',
  joining: () => 'Joining…',
  reconnecting: (attempt) => `Reconnecting… (${attempt})`,
  disconnected: () => 'Disconnected',
};
const SUBS: Record<Exclude<ConnectionState, 'hidden'>, string> = {
  connecting: 'Finding the server',
  joining: 'Hopping into the arena',
  reconnecting: 'Your bean is being held for you',
  disconnected: 'We lost the connection to the room.',
};

export interface ConnectionOverlay { readonly el: HTMLElement; set(state: ConnectionState, attempt?: number): void }

export function createConnectionOverlay(deps: Pick<UiDeps, 'actions'>): ConnectionOverlay {
  const label = el('span', { class: 'conn-label' });
  const sub = el('span', { class: 'conn-sub' });
  const hop = el('div', { class: 'bean-hop' }, beanIcon('#FF4F9A', 'happy', 'ico'));
  const sad = el('div', { class: 'conn-sad' }, beanIcon('#9B6BFF', 'out', 'ico'));
  const back = button({ label: 'BACK TO MENU', icon: 'back', variant: 'pink', onClick: () => deps.actions.leave() });
  const panel = el('div', { class: 'panel conn-panel' }, el('div', { class: 'conn-art' }, hop, el('div', { class: 'conn-shadow' }), sad), label, sub, back);
  const root = el('div', { class: 'screen connection', attrs: { role: 'status', 'aria-live': 'polite' } }, panel);
  root.hidden = true;
  let current: ConnectionState = 'hidden';
  return {
    el: root,
    set(state, attempt = 0) {
      const entering = state !== current;
      current = state;
      setHidden(root, state === 'hidden');
      if (state === 'hidden') return;
      const bad = state === 'disconnected';
      setHidden(hop, bad);
      setHidden(sad, !bad);
      setHidden(back, !bad);
      toggleClass(panel, 'bad', bad);
      setText(label, LABELS[state](attempt));
      setText(sub, SUBS[state]);
      if (entering && bad) back.focus();
    },
  };
}
